/*
 * Register screen — create an account via email + password against the prod API.
 *
 * On success, the API returns full auth tokens and we drop the user straight
 * into the app (no separate "now log in with your new credentials" step).
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { BrandWordmark } from '@/components/BrandWordmark';

export default function Register() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cleanEmail = email.trim().toLowerCase();
    const cleanName = displayName.trim();

    if (!cleanEmail || !password) {
      setError('Email and password are required.');
      return;
    }
    // Display name is required — the server rejects without it anyway,
    // and the label saying "(optional)" while the API errored was a trap
    // Travis hit live. Validate up front with a friendly message.
    if (!cleanName) {
      setError('Display name is required. It\'s how your group sees you on the roster.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.register({
        email: cleanEmail,
        password,
        displayName: cleanName,
      });
      login(
        res.userId,
        res.username,
        res.displayName,
        res.accessToken,
        res.refreshToken,
        stayLoggedIn,
      );
      // If the user landed here via an invite link, send them back to the
      // invite page (which asks for confirmation when claim+token are present)
      // without a second sign-in click. Otherwise
      // check for an email-matched personal invitation before Home.
      const joinCode = new URLSearchParams(window.location.search).get('join');
      if (joinCode) {
        const sp = new URLSearchParams();
        const claim = new URLSearchParams(window.location.search).get('claim');
        const token = new URLSearchParams(window.location.search).get('token');
        if (claim) sp.set('claim', claim);
        if (token) sp.set('token', token);
        const qs = sp.toString();
        navigate(`/app/join/${joinCode}${qs ? `?${qs}` : ''}`, { replace: true });
      } else {
        navigate('/app/invitations?next=%2Fapp%2Fhome', { replace: true });
      }
    } catch (err: any) {
      setError(err.message ?? 'Could not create your account.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <Link to="/" className="auth-brand">
          <BrandWordmark className="brand-wordmark" />
        </Link>

        <h1 className="auth-title">Create your account</h1>
        {new URLSearchParams(window.location.search).get('join') ? (
          <p className="auth-sub">
            One quick sign-up and you'll be dropped into the event you were invited to. We never share your info with anyone outside your event.
          </p>
        ) : (
          <p className="auth-sub">
            Set up 18Gimmies in seconds. No fees, no ads, no nonsense.
          </p>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <label className="auth-label">
            <span>EMAIL</span>
            <input
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="auth-input"
              disabled={loading}
            />
          </label>

          <label className="auth-label">
            <span>DISPLAY NAME</span>
            <input
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="What your buddies call you"
              className="auth-input"
              disabled={loading}
              required
            />
          </label>

          <label className="auth-label">
            <span>PASSWORD</span>
            <div className="auth-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="auth-input auth-input-pad-right"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="auth-eye-btn"
                tabIndex={-1}
              >
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </label>

          <label className="auth-label">
            <span>CONFIRM PASSWORD</span>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              className="auth-input"
              disabled={loading}
            />
          </label>

          <label className="auth-checkbox">
            <input
              type="checkbox"
              checked={stayLoggedIn}
              onChange={(e) => setStayLoggedIn(e.target.checked)}
            />
            <span>Stay signed in</span>
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-cta" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account →'}
          </button>

          <div className="auth-footer">
            <span>Already have an account?</span>{' '}
            <Link
              to={`/app/login${window.location.search}`}
              className="auth-footer-link"
            >
              Sign in
            </Link>
          </div>

          <p className="auth-fineprint">
            By creating an account you agree to our{' '}
            <Link to="/privacy" className="auth-footer-link">
              Privacy Policy
            </Link>
            .
          </p>
        </form>
      </div>
    </div>
  );
}
